import { openDB, DBSchema, IDBPDatabase } from 'idb';

export interface SyncHistoryEntry {
    id: string;
    module: 'tasks' | 'timeclock' | 'compounding';
    description: string;
    method: string;
    url: string;
    status: 'synced' | 'failed';
    errorMessage?: string;
    retryCount: number;
    user?: string;
    queuedAt: string;
    syncedAt: string;
}

interface OfflineDBSchema extends DBSchema {
    offlineQueue: {
        key: string;
        value: {
            id: string;
            method: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
            url: string;
            body?: any;
            headers?: Record<string, string>;
            module: 'tasks' | 'timeclock' | 'compounding';
            description: string;
            createdAt: string;
            retries: number;
        };
        indexes: { 'by-module': string; 'by-created': string };
    };
    cachedData: {
        key: string;
        value: {
            key: string;
            data: any;
            module: string;
            updatedAt: string;
        };
    };
    syncHistory: {
        key: string;
        value: SyncHistoryEntry;
        indexes: { 'by-module': string; 'by-synced': string; 'by-status': string };
    };
}

let dbInstance: IDBPDatabase<OfflineDBSchema> | null = null;

async function getDb() {
    if (dbInstance) return dbInstance;
    dbInstance = await openDB<OfflineDBSchema>('labflo-offline', 2, {
        upgrade(db, oldVersion) {
            if (oldVersion < 1) {
                const queueStore = db.createObjectStore('offlineQueue', { keyPath: 'id' });
                queueStore.createIndex('by-module', 'module');
                queueStore.createIndex('by-created', 'createdAt');
                db.createObjectStore('cachedData', { keyPath: 'key' });
            }
            if (oldVersion < 2) {
                const historyStore = db.createObjectStore('syncHistory', { keyPath: 'id' });
                historyStore.createIndex('by-module', 'module');
                historyStore.createIndex('by-synced', 'syncedAt');
                historyStore.createIndex('by-status', 'status');
            }
        },
    });
    return dbInstance;
}

// ============== QUEUE OPERATIONS ==============

export async function addToQueue(entry: {
    method: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    url: string;
    body?: any;
    headers?: Record<string, string>;
    module: 'tasks' | 'timeclock' | 'compounding';
    description: string;
}) {
    const db = await getDb();
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    await db.put('offlineQueue', {
        ...entry,
        id,
        createdAt: new Date().toISOString(),
        retries: 0,
    });
    return id;
}

export async function getQueue() {
    const db = await getDb();
    return db.getAllFromIndex('offlineQueue', 'by-created');
}

export async function getQueueCount() {
    const db = await getDb();
    return db.count('offlineQueue');
}

export async function getQueueByModule(module: string) {
    const db = await getDb();
    return db.getAllFromIndex('offlineQueue', 'by-module', module);
}

export async function removeFromQueue(id: string) {
    const db = await getDb();
    await db.delete('offlineQueue', id);
}

export async function clearQueue() {
    const db = await getDb();
    await db.clear('offlineQueue');
}

export async function incrementRetry(id: string) {
    const db = await getDb();
    const entry = await db.get('offlineQueue', id);
    if (entry) {
        entry.retries += 1;
        await db.put('offlineQueue', entry);
    }
}

// ============== CACHE OPERATIONS ==============

export async function cacheData(key: string, data: any, module: string) {
    const db = await getDb();
    await db.put('cachedData', {
        key,
        data,
        module,
        updatedAt: new Date().toISOString(),
    });
}

export async function getCachedData(key: string) {
    const db = await getDb();
    const entry = await db.get('cachedData', key);
    return entry?.data || null;
}

export async function clearCachedData(module?: string) {
    const db = await getDb();
    if (!module) {
        await db.clear('cachedData');
        return;
    }
    const all = await db.getAll('cachedData');
    for (const entry of all) {
        if (entry.module === module) {
            await db.delete('cachedData', entry.key);
        }
    }
}

// ============== SYNC HISTORY OPERATIONS ==============

export async function addSyncHistoryEntry(entry: Omit<SyncHistoryEntry, 'id'>) {
    const db = await getDb();
    const id = `sh-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    await db.put('syncHistory', { ...entry, id });
    return id;
}

export async function getSyncHistory(limit?: number): Promise<SyncHistoryEntry[]> {
    const db = await getDb();
    const all = await db.getAllFromIndex('syncHistory', 'by-synced');
    const sorted = all.reverse(); // newest first
    return limit ? sorted.slice(0, limit) : sorted;
}

export async function getSyncHistoryByModule(module: string): Promise<SyncHistoryEntry[]> {
    const db = await getDb();
    const entries = await db.getAllFromIndex('syncHistory', 'by-module', module);
    return entries.reverse();
}

export async function getSyncHistoryCount(): Promise<number> {
    const db = await getDb();
    return db.count('syncHistory');
}

export async function clearSyncHistory() {
    const db = await getDb();
    await db.clear('syncHistory');
}

